import React from 'react';
import { Box, Card, Typography, Button, styled, Theme, useTheme } from '@mui/material';
import AlarmIcon from '@mui/icons-material/Alarm';
import WarningIcon from '@mui/icons-material/Warning';

export interface AlertCardProps {
  /** Título del aviso */
  title: string;
  /** Contador principal */
  counter: number | string;
  /** Fecha o texto de aviso secundario */
  secondaryText: string;
  /** Tipo de alerta */
  type?: 'warning' | 'info' | 'danger';
  /** Función para manejar el click en el botón */
  onAction?: () => void;
  /** Texto del botón de acción */
  actionText?: string;
}

// Estilos según tipo de alerta
const getColorsByType = (theme: Theme, type: string) => {
  switch (type) {
    case 'warning':
      return {
        background: `linear-gradient(135deg, ${theme.palette.warning.light} 0%, ${theme.palette.warning.main} 100%)`,
        color: theme.palette.warning.contrastText,
        icon: WarningIcon
      };
    case 'danger':
      return {
        background: `linear-gradient(135deg, ${theme.palette.error.light} 0%, ${theme.palette.error.main} 100%)`,
        color: theme.palette.error.contrastText,
        icon: WarningIcon
      };
    case 'info':
    default:
      return {
        background: `linear-gradient(135deg, ${theme.palette.info.light} 0%, ${theme.palette.info.main} 100%)`,
        color: theme.palette.info.contrastText,
        icon: AlarmIcon
      };
  }
};

const AlertIconContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  width: 56,
  height: 56,
  borderRadius: '50%',
  backgroundColor: 'rgba(255, 255, 255, 0.2)',
  marginBottom: theme.spacing(2),
}));

/**
 * Componente AlertCard para mostrar avisos importantes con contador y fecha
 * 
 * Muestra avisos y alertas con un estilo visual destacado
 */
export const AlertCard: React.FC<AlertCardProps> = ({
  title,
  counter,
  secondaryText,
  type = 'info',
  onAction,
  actionText = 'Ver detalles'
}) => {
  const theme = useTheme();
  const colors = getColorsByType(theme, type);
  const IconComponent = colors.icon;

  return (
    <Card
      sx={{
        p: 3,
        borderRadius: 2,
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        color: colors.color,
        background: colors.background,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <AlertIconContainer>
        <IconComponent sx={{ fontSize: 28, color: 'inherit' }} />
      </AlertIconContainer>
      
      <Typography variant="h6" gutterBottom fontWeight={500}>
        {title}
      </Typography>
      
      <Typography variant="h3" component="div" fontWeight={600} sx={{ mb: 1 }}>
        {counter}
      </Typography>
      
      <Typography variant="body1" sx={{ mb: 2, opacity: 0.9 }}>
        {secondaryText}
      </Typography>
      
      {onAction && (
        <Box sx={{ mt: 'auto' }}>
          <Button 
            variant="contained" 
            fullWidth
            sx={{ 
              bgcolor: 'rgba(255, 255, 255, 0.2)',
              color: 'inherit',
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.3)',
              }
            }}
            onClick={onAction}
          >
            {actionText}
          </Button>
        </Box>
      )}
    </Card>
  );
};

export default AlertCard;
